import React, {useEffect, useState} from "react";
import {styled} from "@mui/system";
import {CheckCircleOutline, Done, MoreVert, PaymentsOutlined, WarningAmber} from "@mui/icons-material";
import {DataGrid} from "@mui/x-data-grid";
import Chip from "@mui/material/Chip";
import {useNavigate} from "react-router-dom";
import {TextField, Typography} from "@material-ui/core";
import {ApiDetails} from "../../../../Constants";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import SendIcon from "@mui/icons-material/Send";
import Slide from "@mui/material/Slide";
import {showSnackbar} from "../../../../components/SnackBar/SnackBar";
import Box from "@mui/material/Box";
import WaterDropIcon from "@mui/icons-material/Water";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import CircularProgress from "@mui/material/CircularProgress";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import {alpha, useTheme} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CloseIcon from "@mui/icons-material/Close";
import CardBillingConfirmationDialog from "../../../tenant/tenant components/cardBillingConfirmatinDialog";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import {DoneOutlined} from "@material-ui/icons";
import {ErrorIcon} from "react-hot-toast";
import { handle401Unauthorized, handle403Forbidden } from '../../../../utils/authErrorHandler';
import { getUserData } from '../../../../services/api/client';


// Enhanced DataGrid styling - fixed header overlap issue
const StyledDataGrid = styled(DataGrid)(({theme}) => ({
    "& .MuiDataGrid-root": {
        height: "100%",
    },
    "& .MuiDataGrid-columnHeaders": {
        fontSize: "1rem",
        fontWeight: 700,
        backgroundColor: "#2C5283",
        color: "#FFFFFF",
        zIndex: 5,
    },
    "& .MuiDataGrid-row:nth-of-type(even)": {
        backgroundColor: "#f7f9fc",
    },
    "& .MuiDataGrid-row:hover": {
        backgroundColor: "#e8f0fe",
        cursor: "pointer", // Add cursor pointer on hover
    },
    "& .MuiDataGrid-cell:focus": {
        outline: "none",
    },
    "& .MuiDataGrid-cell": {
        fontSize: "0.9rem",
        padding: "12px 16px",
        borderBottom: "1px solid #eaeaea",
    },
    "& .MuiDataGrid-footerContainer": {
        borderTop: "1px solid #eaeaea",
        backgroundColor: "#f7f9fc",
    },
    "& .MuiDataGrid-columnHeaderRow": {
        minHeight: "56px !important",
    },
    "& .MuiDataGrid-row:first-of-type": {
        paddingTop: "4px",
    },
    "& .MuiDataGrid-virtualScroller": {
        marginTop: "56px !important",
    },
    // Make entire row clickable
    "& .MuiDataGrid-row": {
        cursor: "pointer",
        transition: "background-color 0.2s ease", // Smooth transition
    }
}));

const SearchBox = styled(TextField)({
    "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        backgroundColor: "#fff",
        "& fieldset": {
            borderColor: "#e0e0e0",
        },
        "&:hover fieldset": {
            borderColor: "#2C5283",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#2C5283",
        },
    },
    "& .MuiInputLabel-root": {
        fontSize: "0.95rem",
    }
});

// Slide transition for smoother opening
const Transition = React.forwardRef((props, ref) => (
    <Slide direction="up" ref={ref} {...props} />
));

const StatusChip = styled(Chip)(({statusType}) => ({
    borderRadius: '16px',
    fontWeight: 500,
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
}));

// Enhanced styled components with more modern elements
const styles = {
    dialogTitle: {
        backgroundColor: "#2C5283",
        color: "white",
        padding: "16px 24px",
        fontWeight: "bold",
        display: "flex",
        alignItems: "center",
        gap: "10px",
    },
    dialogContent: {
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        // Ensure contents fit without requiring scrolling
        overflow: "visible",
    },
    container: {
        width: "100%",
        maxWidth: "90vw",
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
        overflow: "visible", // Allow content to expand fully
    },
    billingHeader: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginBottom: "20px",
    },
    label: {
        fontSize: "1rem",
        fontWeight: 600,
        color: "#444",
    },
    cancelButton: {
        color: "#d32f2f",
        borderColor: "#d32f2f",
        borderRadius: "8px",
        padding: "10px 24px",
        textTransform: "none",
        fontWeight: 600,
        "&:hover": {
            backgroundColor: "rgba(211, 47, 47, 0.1)",
        },
    },
    billButton: {
        backgroundColor: "#2C5283",
        color: "white",
        borderRadius: "8px",
        padding: "10px 24px",
        textTransform: "none",
        fontWeight: 600,
        "&:hover": {
            backgroundColor: "#1E3A63",
        },
    },
    downloadButton: {
        backgroundColor: "#388e3c",
        color: "white",
        borderRadius: "8px",
        padding: "10px 20px",
        textTransform: "none",
        fontWeight: 600,
        "&:hover": {
            backgroundColor: "#2e7d32",
        },
        display: "flex",
        alignItems: "center",
        gap: "8px",
    },
    monthSelection: {
        padding: "20px",
        backgroundColor: "#f7f9fc",
        borderRadius: "12px",
        marginBottom: "24px",
        border: "1px solid #e0e7ff",
    },
    infoBox: {
        padding: "16px",
        backgroundColor: "#e3f2fd",
        borderRadius: "8px",
        marginTop: "8px",
        marginBottom: "16px",
        border: "1px solid #bbdefb",
    },
    statusChip: {
        fontWeight: 600,
        borderRadius: "16px",
    },
    tableContainer: {
        marginTop: "16px",
        padding: "16px",
        backgroundColor: "#f7f9fc",
        borderRadius: "8px",
    },
    // Add a wrapper style for the DataGrid to control height
    gridWrapper: {
        height: "auto",
        width: "100%",
        marginBottom: "20px",
    },
    searchHeader: {
        padding: "12px 16px",
        backgroundColor: "#f5f7fa",
        borderRadius: "8px",
        border: "1px solid #e0e0e0",
        marginBottom: "16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
    },
};

const PropertyTenants = (props) => {
    const navigate = useNavigate();
    const theme = useTheme();

    const {
        originalText,
        waterBill,
        minimumWaterBill,
        electricityBill,
        minimumElectricityBill,
        propertyDetails,
        viewModal
    } = props;

    // Helper function to get auth token
    const getAuthToken = () => {
        const userData = getUserData();
        return userData ? userData.find(([key]) => key === 'access_token')?.[1] : null;
    };

    // State definitions
    let [billedTenants, setBilledTenants] = useState([]);
    let [elecBilledTenants, setElecBilledTenants] = useState([]);
    // const [removedWaterBilling, setRemovedWaterBilling] = useState([]);
    // const [removedElectricityBilling, setRemovedElectricityBilling] = useState([]);
    const [month, setMonth] = useState(getBillingMonth());
    const [selectedMonth, setSelectedMonth] = useState(getBillingMonth());
    const [tenants, setTenants] = useState([]);
    const [update,] = useState(false);
    const [tenantsLength, setTenantsLength] = useState(0);
    const [htmlContent, setHtmlContent] = useState('');
    const [billLoading, setBillLoading] = useState(false);

    // NEW: Add enhanced state management like in BillTenantsDialog
    const [tenantUtilitySettings, setTenantUtilitySettings] = useState({});
    const [tenantBillingStatus, setTenantBillingStatus] = useState({});

    // NEW: Add confirmation dialog state
    const [showBillingConfirmDialog, setShowBillingConfirmDialog] = useState(false);
    const [billingDataForConfirm, setBillingDataForConfirm] = useState(null);

    // Search state
    const [fetched, setFetched] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filteredTenants, setFilteredTenants] = useState([]);

    const billTenantsModal = viewModal;

    function getMonthName(monthNumber) {
        const date = new Date();
        date.setMonth(monthNumber);

        return date.toLocaleString("en-US", {month: "long"});
    }

    function getBillingMonth() {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth(); // 0-based index (Jan = 0, Feb = 1, etc.)
        const day = today.getDate();

        if (day >= new Date(year, month + 1, 0).getDate() - 4 || day <= 10) {
            // Last 5 days of the month OR first 10 days of the new month
            return new Date(year, month + 1, 1); // Select Next Month
        } else {
            return new Date(year, month, 1); // Select Current Month
        }
    }

    // Search functionality
    useEffect(() => {
        if (searchTerm.trim() === "") {
            setFilteredTenants(tenants);
            return;
        }

        const lowercasedSearch = searchTerm.toLowerCase();
        const filtered = tenants.filter(tenant =>
            // Search by name
            (tenant.first_name?.toLowerCase().includes(lowercasedSearch) ||
                tenant.last_name?.toLowerCase().includes(lowercasedSearch) ||
                // Search by house number
                tenant.house_number?.toLowerCase().includes(lowercasedSearch) ||
                // Search by phone number
                tenant.phone_number?.toLowerCase().includes(lowercasedSearch))
        );

        setFilteredTenants(filtered);
    }, [searchTerm, tenants]);

    // Handle search input change
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    // Clear search
    const handleClearSearch = () => {
        setSearchTerm("");
    };

    useEffect(() => {



        fetch(ApiDetails + "pegasus/visionary/tenant/get/property/tenants", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": getAuthToken() || ""
            },
            body: JSON.stringify({property_id: propertyDetails.property_id}),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 403  && propertyDetails.property_id !== null && propertyDetails.property_id !== "") {

                    localStorage.clear();
                    window.location.href = "/";
                    return;
                }
                if (response.status === 200) {
                    setTenantsLength(data.length);
                    setTenants(data);
                    setFetched(true);

                    // NEW: Initialize utility settings and billing status for each tenant
                    const initialSettings = {};
                    const initialBillingStatus = {};
                    data.forEach(tenant => {
                        initialSettings[tenant.tenant_id] = {
                            hasWater: true,
                            hasElectricity: true
                        };
                        // Include all tenants in billing by default (user can toggle off in UI)
                        initialBillingStatus[tenant.tenant_id] = true;
                    });
                    setTenantUtilitySettings(initialSettings);
                    setTenantBillingStatus(initialBillingStatus);
                }
            })
            .catch((err) => console.log(err));
    }, [update, propertyDetails]);

    // Get row styling based on billing enabled status
    const getRowStyle = (params) => {
        const isIncludedInBilling = tenantBillingStatus[params.row.tenant_id];
        const isEnabled = params.row.billing_enabled && isIncludedInBilling;

        return {
            backgroundColor: isEnabled ? "transparent" : "#f0f0f0",
            color: isEnabled ? "inherit" : "#888",
            pointerEvents: isEnabled ? "auto" : "none",
            opacity: isEnabled ? 1 : 0.6,
        };
    };

    // NEW: Enhanced utility handling functions from BillTenantsDialog
    const handleWaterReadingChange = (tenantId, value, tenant) => {
        if (!value) return;

        let filteredTenantArray = billedTenants.filter((t) => t.tenant_id !== tenantId);

        let consumedUnits = value - tenant.meter_reading;
        consumedUnits = consumedUnits.toFixed(1);

        let finalWaterBill = parseFloat(consumedUnits) * parseInt(waterBill);

        if (finalWaterBill < minimumWaterBill) {
            finalWaterBill = minimumWaterBill;
        }

        setBilledTenants([...filteredTenantArray, {
            tenant_id: tenantId,
            rent: parseInt(tenant.rent.toString().replace(",", "")),
            arrears: parseInt(tenant.arrears),
            finalWaterBill: parseFloat(finalWaterBill),
            total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(finalWaterBill),
            meter_reading: value,
            landlord_id: JSON.parse(originalText)[0][1],
        }]);
    };

    const handleElectricityReadingChange = (tenantId, value, tenant) => {
        if (!value) return;

        let filteredTenantArray = elecBilledTenants.filter((t) => t.tenant_id !== tenantId);

        let consumedUnits = value - tenant.electricity_meter_reading;
        consumedUnits = consumedUnits.toFixed(1);

        let finalElecBill = parseFloat(consumedUnits) * parseInt(electricityBill);

        if (finalElecBill < minimumElectricityBill) {
            finalElecBill = minimumElectricityBill;
        }

        setElecBilledTenants([...filteredTenantArray, {
            tenant_id: tenantId,
            rent: parseInt(tenant.rent.toString().replace(",", "")),
            arrears: parseInt(tenant.arrears),
            electricity_bill: parseFloat(finalElecBill),
            total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(finalElecBill),
            electricity_meter_reading: value,
            landlord_id: JSON.parse(originalText)[0][1],
        }]);
    };

    // NEW: Utility toggle functions from BillTenantsDialog
    const handleUtilityToggle = (tenantId, utilityType, enabled) => {
        setTenantUtilitySettings(prev => ({
            ...prev,
            [tenantId]: {
                ...prev[tenantId],
                [utilityType]: enabled
            }
        }));

        // Only apply billing changes if tenant is included in billing
        const isIncludedInBilling = tenantBillingStatus[tenantId];
        if (!isIncludedInBilling) return;

        if (!enabled) {
            // If utility is disabled, apply minimum charge
            const tenant = tenants.find(t => t.tenant_id === tenantId);
            if (tenant) {
                if (utilityType === 'hasWater') {
                    setBilledTenants(prev => {
                        const filtered = prev.filter(t => t.tenant_id !== tenantId);
                        return [...filtered, {
                            tenant_id: tenantId,
                            rent: parseInt(tenant.rent.toString().replace(",", "")),
                            arrears: parseInt(tenant.arrears),
                            finalWaterBill: parseFloat(minimumWaterBill || 0),
                            total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(minimumWaterBill || 0),
                            meter_reading: tenant.meter_reading,
                            landlord_id: JSON.parse(originalText)[0][1],
                            isMinimumCharge: true
                        }];
                    });
                } else if (utilityType === 'hasElectricity') {
                    setElecBilledTenants(prev => {
                        const filtered = prev.filter(t => t.tenant_id !== tenantId);
                        return [...filtered, {
                            tenant_id: tenantId,
                            rent: parseInt(tenant.rent.toString().replace(",", "")),
                            arrears: parseInt(tenant.arrears),
                            electricity_bill: parseFloat(minimumElectricityBill || 0),
                            total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(minimumElectricityBill || 0),
                            electricity_meter_reading: tenant.electricity_meter_reading,
                            landlord_id: JSON.parse(originalText)[0][1],
                            isMinimumCharge: true
                        }];
                    });
                }
            }
        } else {
            // If re-enabled, remove from billed arrays to allow manual input
            if (utilityType === 'hasWater') {
                setBilledTenants(prev => prev.filter(t => t.tenant_id !== tenantId));
            } else if (utilityType === 'hasElectricity') {
                setElecBilledTenants(prev => prev.filter(t => t.tenant_id !== tenantId));
            }
        }
    };

    // NEW: Billing status toggle function from BillTenantsDialog
    const handleBillingStatusToggle = (tenantId, included) => {
        setTenantBillingStatus(prev => ({
            ...prev,
            [tenantId]: included
        }));

        if (!included) {
            // Remove from billing arrays if excluded
            setBilledTenants(prev => prev.filter(t => t.tenant_id !== tenantId));
            setElecBilledTenants(prev => prev.filter(t => t.tenant_id !== tenantId));
        } else {
            // If including back, check if utilities are disabled and apply minimum charges
            const tenant = tenants.find(t => t.tenant_id === tenantId);
            const settings = tenantUtilitySettings[tenantId] || {};

            if (tenant) {
                // Auto-apply minimum charges for disabled utilities when including tenant
                if (!settings.hasWater) {
                    setBilledTenants(prev => {
                        if (!prev.find(t => t.tenant_id === tenantId)) {
                            const finalWaterBill = minimumWaterBill || 0;
                            return [...prev, {
                                tenant_id: tenantId,
                                rent: parseInt(tenant.rent.toString().replace(",", "")),
                                arrears: parseInt(tenant.arrears),
                                finalWaterBill: parseFloat(finalWaterBill),
                                total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(finalWaterBill),
                                meter_reading: tenant.meter_reading,
                                landlord_id: JSON.parse(originalText)[0][1],
                                isMinimumCharge: true
                            }];
                        }
                        return prev;
                    });
                }

                if (!settings.hasElectricity) {
                    setElecBilledTenants(prev => {
                        if (!prev.find(t => t.tenant_id === tenantId)) {
                            const finalElecBill = minimumElectricityBill || 0;
                            return [...prev, {
                                tenant_id: tenantId,
                                rent: parseInt(tenant.rent.toString().replace(",", "")),
                                arrears: parseInt(tenant.arrears),
                                electricity_bill: parseFloat(finalElecBill),
                                total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(finalElecBill),
                                electricity_meter_reading: tenant.electricity_meter_reading,
                                landlord_id: JSON.parse(originalText)[0][1],
                                isMinimumCharge: true
                            }];
                        }
                        return prev;
                    });
                }
            }
        }
    };

    // NEW: Bulk utility toggle functions from BillTenantsDialog
    const handleBulkUtilityToggle = (utilityType, enabled) => {
        const updatedSettings = {...tenantUtilitySettings};

        // Get only included tenants that are billing enabled
        const eligibleTenants = tenants.filter(tenant =>
            tenant.billing_enabled && tenantBillingStatus[tenant.tenant_id]
        );

        // Update all utility settings first
        eligibleTenants.forEach(tenant => {
            updatedSettings[tenant.tenant_id] = {
                ...updatedSettings[tenant.tenant_id],
                [utilityType]: enabled
            };
        });

        // Apply the updated settings
        setTenantUtilitySettings(updatedSettings);

        if (!enabled) {
            // Apply minimum charges for disabled utilities
            if (utilityType === 'hasWater') {
                setBilledTenants(prevBilled => {
                    let updatedBilled = prevBilled.filter(existing =>
                        !eligibleTenants.some(tenant => tenant.tenant_id === existing.tenant_id)
                    );

                    const newBilledEntries = eligibleTenants.map(tenant => ({
                        tenant_id: tenant.tenant_id,
                        rent: parseInt(tenant.rent.toString().replace(",", "")),
                        arrears: parseInt(tenant.arrears),
                        finalWaterBill: parseFloat(minimumWaterBill || 0),
                        total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(minimumWaterBill || 0),
                        meter_reading: tenant.meter_reading,
                        landlord_id: JSON.parse(originalText)[0][1],
                        isMinimumCharge: true
                    }));

                    return [...updatedBilled, ...newBilledEntries];
                });
            } else if (utilityType === 'hasElectricity') {
                setElecBilledTenants(prevBilled => {
                    let updatedBilled = prevBilled.filter(existing =>
                        !eligibleTenants.some(tenant => tenant.tenant_id === existing.tenant_id)
                    );

                    const newBilledEntries = eligibleTenants.map(tenant => ({
                        tenant_id: tenant.tenant_id,
                        rent: parseInt(tenant.rent.toString().replace(",", "")),
                        arrears: parseInt(tenant.arrears),
                        electricity_bill: parseFloat(minimumElectricityBill || 0),
                        total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(minimumElectricityBill || 0),
                        electricity_meter_reading: tenant.electricity_meter_reading,
                        landlord_id: JSON.parse(originalText)[0][1],
                        isMinimumCharge: true
                    }));

                    return [...updatedBilled, ...newBilledEntries];
                });
            }
        } else {
            // Remove from billing arrays if re-enabled to allow manual input
            if (utilityType === 'hasWater') {
                setBilledTenants(prev => prev.filter(t =>
                    !eligibleTenants.some(tenant => tenant.tenant_id === t.tenant_id)
                ));
            } else if (utilityType === 'hasElectricity') {
                setElecBilledTenants(prev => prev.filter(t =>
                    !eligibleTenants.some(tenant => tenant.tenant_id === t.tenant_id)
                ));
            }
        }
    };

    // NEW: Bulk billing toggle function from BillTenantsDialog
    const handleBulkBillingToggle = (included) => {
        const updatedStatus = {};

        tenants.forEach(tenant => {
            if (tenant.billing_enabled) {
                updatedStatus[tenant.tenant_id] = included;
            }
        });

        setTenantBillingStatus(prev => ({...prev, ...updatedStatus}));

        if (!included) {
            // Remove ALL tenants from billing arrays if excluded
            setBilledTenants(prev => prev.filter(t =>
                !tenants.some(tenant => tenant.billing_enabled && tenant.tenant_id === t.tenant_id)
            ));
            setElecBilledTenants(prev => prev.filter(t =>
                !tenants.some(tenant => tenant.billing_enabled && tenant.tenant_id === t.tenant_id)
            ));
        } else {
            // When including all, apply minimum charges for tenants with disabled utilities
            const eligibleTenants = tenants.filter(tenant => tenant.billing_enabled);

            // Handle water billing for tenants with disabled water
            setBilledTenants(prev => {
                const tenantsNeedingWaterMinimum = eligibleTenants.filter(tenant => {
                    const settings = tenantUtilitySettings[tenant.tenant_id] || {};
                    return !settings.hasWater && !prev.find(t => t.tenant_id === tenant.tenant_id);
                });

                const newWaterBills = tenantsNeedingWaterMinimum.map(tenant => ({
                    tenant_id: tenant.tenant_id,
                    rent: parseInt(tenant.rent.toString().replace(",", "")),
                    arrears: parseInt(tenant.arrears),
                    finalWaterBill: parseFloat(minimumWaterBill || 0),
                    total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(minimumWaterBill || 0),
                    meter_reading: tenant.meter_reading,
                    landlord_id: JSON.parse(originalText)[0][1],
                    isMinimumCharge: true
                }));

                return [...prev, ...newWaterBills];
            });

            // Handle electricity billing for tenants with disabled electricity
            setElecBilledTenants(prev => {
                const tenantsNeedingElectricityMinimum = eligibleTenants.filter(tenant => {
                    const settings = tenantUtilitySettings[tenant.tenant_id] || {};
                    return !settings.hasElectricity && !prev.find(t => t.tenant_id === tenant.tenant_id);
                });

                const newElectricityBills = tenantsNeedingElectricityMinimum.map(tenant => ({
                    tenant_id: tenant.tenant_id,
                    rent: parseInt(tenant.rent.toString().replace(",", "")),
                    arrears: parseInt(tenant.arrears),
                    electricity_bill: parseFloat(minimumElectricityBill || 0),
                    total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(minimumElectricityBill || 0),
                    electricity_meter_reading: tenant.electricity_meter_reading,
                    landlord_id: JSON.parse(originalText)[0][1],
                    isMinimumCharge: true
                }));

                return [...prev, ...newElectricityBills];
            });
        }
    };

    function stringToColor(string) {
        let hash = 0;
        let i;
        for (i = 0; i < string.length; i += 1) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        let color = '#';
        for (i = 0; i < 3; i += 1) {
            const value = (hash >> (i * 8)) & 0xff;
            color += `00${value.toString(16)}`.slice(-2);
        }
        return color;
    }

    function stringAvatar(name) {
        return {
            sx: {
                bgcolor: stringToColor(name),
            },
            children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
        };
    }

    // Enhanced columns with icons and better styling
    const columns = [
        {
            field: "user",
            headerName: "Names",
            minWidth: 200,
            flex: 1,
            renderCell: (params) => (
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        borderRadius: "3px",
                        backgroundColor: params.row.state === "NOTICE" ? "#fff8e1" : "",
                    }}
                    className="userListUser"
                >
                    <Tooltip title={params.row.state === "NOTICE" ? "Notice" : "Active"}>
                        <div style={{margin: "0 12px 0 0"}}>
                            <Avatar
                                {...stringAvatar(params.row.first_name.toString() + " " + params.row.last_name.toString())}
                                style={{width: 40, height: 40, fontSize: "16px", fontWeight: "bold"}}
                            />
                        </div>
                    </Tooltip>
                    <div>
                        <Typography variant="body1" style={{fontWeight: 500}}>
                            {params.row.first_name} {params.row.last_name}
                        </Typography>
                        <Typography variant="caption" color="primary">
                            {params.row.phone_number}
                        </Typography>
                    </div>
                </div>
            ),
        },
        {
            field: "house_number",
            headerName: "House No.",
            minWidth: 120,
            flex: 0,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    size="small"
                    style={{
                        backgroundColor: "#e3f2fd",
                        color: "#1976d2",
                        fontWeight: 500,
                    }}
                />
            ),
        },
        {
            field: "rent",
            headerName: "Rent",
            minWidth: 120,
            flex: 1,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <span style={{fontWeight: 600, color: "#2E7D32"}}>
                    {parseInt(params.row.rent.toString().replace(",", "")).toLocaleString("en-US", {
                        style: "currency",
                        currency: "KSH",
                    })}
                </span>
            ),
        },
        {
            field: "bill",
            headerName: "Wallet",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <span style={{fontWeight: 600, color: params.row.status === "PENDING" ? "#D32F2F" : "#388E3C"}}>
                    {params.row.Wallet.toLocaleString("en-US", {
                        style: "currency",
                        currency: "KSH",
                    })}
                </span>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            minWidth: 120,
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => {
                switch (params.row.status) {
                    case "PENDING":
                        return <Chip label="PENDING" color="error" variant="outlined" sx={styles.statusChip}/>;
                    case "COMPLETED":
                        return <Chip label="PAID" color="success" variant="outlined" icon={<Done/>}
                                     sx={styles.statusChip}/>;
                    default:
                        return <Chip label="PARTIAL" color="warning" variant="outlined" sx={styles.statusChip}/>;
                }
            },
        },
        {
            field: "login_successful",
            headerName: "App Access",
            flex: 0.8,
            minWidth: 100,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <div className="userListAction">
                    {params.row.login_successful ? (
                        <StatusChip
                            label="Active"
                            color="success"
                            size="small"
                            variant="outlined"
                            statusType="success"
                            icon={<DoneOutlined style={{fontSize: 16}}/>}
                        />
                    ) : (
                        <StatusChip
                            label="Inactive"
                            color="error"
                            size="small"
                            variant="outlined"
                            statusType="error"
                            icon={<ErrorIcon style={{fontSize: 16}}/>}
                        />
                    )}
                </div>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 250,
            flex: 1.2,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <>
                    {params.row.agency_id !== 0 && params.row.agency_id !== JSON.parse(originalText)[0][1] ? (
                        <></>
                    ) : (
                        <Box sx={{display: "flex", gap: 1}}>
                            <Button
                                variant="outlined"
                                color="info"
                                size="small"
                                startIcon={<MoreVert/>}
                                onClick={() => {
                                    navigate("/tenant/" + params.row.tenant_id, {
                                        state: {
                                            tenant_id: params.row.tenant_id,
                                            property_id: params.row.property_id,
                                        },
                                    });
                                }}
                                style={{borderRadius: 20}}
                            >
                                Details
                            </Button>
                        </Box>
                    )}
                </>
            ),
        }
    ];

    // UPDATED: Enhanced billing columns similar to BillTenantsDialog
    const billingColumns = [
        {
            field: "billing_status",
            headerName: "Include",
            flex: 0.5,
            minWidth: 80,
            renderCell: (params) => (
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                    <Switch
                        size="small"
                        checked={tenantBillingStatus[params.row.tenant_id] || false}
                        onChange={(e) => handleBillingStatusToggle(params.row.tenant_id, e.target.checked)}
                        disabled={!params.row.billing_enabled}
                        color="primary"
                    />
                </Box>
            ),
        },
        {
            field: "user",
            headerName: "Name",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <div style={getRowStyle(params)}>
                    {params.row.first_name} {params.row.last_name}
                </div>
            ),
        },
        {
            field: "house_number",
            headerName: "Unit",
            flex: 0.8,
            minWidth: 80,
            renderCell: (params) => (
                <div style={getRowStyle(params)}>{params.row.house_number}</div>
            ),
        },
        {
            field: "rent",
            headerName: "Rent",
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <div style={{...getRowStyle(params), color: "green", fontWeight: 600}}>
                    KSH {parseInt(params.row.rent.toString().replace(",", "")).toLocaleString()}
                </div>
            ),
        },
        {
            field: "utilities_toggle",
            headerName: "Utilities",
            flex: 1,
            minWidth: 130,
            renderCell: (params) => {
                const settings = tenantUtilitySettings[params.row.tenant_id] || {};
                const isIncludedInBilling = tenantBillingStatus[params.row.tenant_id];
                const isEnabled = params.row.billing_enabled && isIncludedInBilling;

                return (
                    <Box sx={{display: "flex", flexDirection: "column", gap: 0.3, ...getRowStyle(params)}}>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={settings.hasWater || false}
                                    onChange={(e) => handleUtilityToggle(params.row.tenant_id, 'hasWater', e.target.checked)}
                                    disabled={!isEnabled}
                                />
                            }
                            label={
                                <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                    <WaterDropIcon sx={{color: "#2196F3", fontSize: 14}}/>
                                    <Typography variant="caption" sx={{fontSize: '0.7rem'}}>Water</Typography>
                                </Box>
                            }
                            sx={{margin: 0, height: 20}}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={settings.hasElectricity || false}
                                    onChange={(e) => handleUtilityToggle(params.row.tenant_id, 'hasElectricity', e.target.checked)}
                                    disabled={!isEnabled}
                                />
                            }
                            label={
                                <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                    <ElectricBoltIcon sx={{color: "#FF9800", fontSize: 14}}/>
                                    <Typography variant="caption" sx={{fontSize: '0.7rem'}}>Electricity</Typography>
                                </Box>
                            }
                            sx={{margin: 0, height: 20}}
                        />
                    </Box>
                );
            },
        },
        {
            field: "meter_reading",
            headerName: "Water Reading",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                const settings = tenantUtilitySettings[params.row.tenant_id] || {};
                return (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, ...getRowStyle(params)}}>
                        <WaterDropIcon
                            fontSize="small"
                            sx={{
                                color: settings.hasWater ? "#2196F3" : "#ccc"
                            }}
                        />
                        <span style={{color: settings.hasWater ? 'inherit' : '#ccc'}}>
                            {params.row.meter_reading}
                        </span>
                        {!settings.hasWater && (
                            <Chip
                                label="Min"
                                size="small"
                                variant="outlined"
                                sx={{fontSize: '0.6rem', height: 16}}
                            />
                        )}
                    </Box>
                );
            },
        },
        {
            field: "current_meter_reading",
            headerName: "New Water Reading",
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                const settings = tenantUtilitySettings[params.row.tenant_id] || {};
                const isIncludedInBilling = tenantBillingStatus[params.row.tenant_id];
                const isWaterEnabled = settings.hasWater && isIncludedInBilling;
                const isEnabled = params.row.billing_enabled && isIncludedInBilling;

                return isWaterEnabled ? (
                    <TextField
                        type="number"
                        placeholder="Enter Reading..."
                        onChange={(e) => handleWaterReadingChange(params.row.tenant_id, e.target.value, params.row)}
                        disabled={!isEnabled}
                        InputProps={{
                            startAdornment: <WaterDropIcon sx={{color: "#2196F3", mr: 1}}/>
                        }}
                        variant="outlined"
                        size="small"
                        sx={{backgroundColor: "white", ...getRowStyle(params)}}
                    />
                ) : (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, ...getRowStyle(params)}}>
                        <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.8rem'}}>
                            KSH {minimumWaterBill || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.7rem'}}>
                            (min)
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: "electricity_meter_reading",
            headerName: "Electricity Reading",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                const settings = tenantUtilitySettings[params.row.tenant_id] || {};
                return (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, ...getRowStyle(params)}}>
                        <ElectricBoltIcon
                            fontSize="small"
                            sx={{
                                color: settings.hasElectricity ? "#FF9800" : "#ccc"
                            }}
                        />
                        <span style={{color: settings.hasElectricity ? 'inherit' : '#ccc'}}>
                            {params.row.electricity_meter_reading}
                        </span>
                        {!settings.hasElectricity && (
                            <Chip
                                label="Min"
                                size="small"
                                variant="outlined"
                                sx={{fontSize: '0.6rem', height: 16}}
                            />
                        )}
                    </Box>
                );
            },
        },
        {
            field: "current_electricity_meter_reading",
            headerName: "New Electricity Reading",
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                const settings = tenantUtilitySettings[params.row.tenant_id] || {};
                const isIncludedInBilling = tenantBillingStatus[params.row.tenant_id];
                const isElectricityEnabled = settings.hasElectricity && isIncludedInBilling;
                const isEnabled = params.row.billing_enabled && isIncludedInBilling;

                return isElectricityEnabled ? (
                    <TextField
                        type="number"
                        placeholder="Enter Reading..."
                        onChange={(e) => handleElectricityReadingChange(params.row.tenant_id, e.target.value, params.row)}
                        disabled={!isEnabled}
                        InputProps={{
                            startAdornment: <ElectricBoltIcon sx={{color: "#FF9800", mr: 1}}/>
                        }}
                        variant="outlined"
                        size="small"
                        sx={{backgroundColor: "white", ...getRowStyle(params)}}
                    />
                ) : (
                    <Box sx={{display: "flex", alignItems: "center", gap: 1, ...getRowStyle(params)}}>
                        <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.8rem'}}>
                            KSH {minimumElectricityBill || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{fontSize: '0.7rem'}}>
                            (min)
                        </Typography>
                    </Box>
                );
            },
        },
    ];

    const handleClose = () => {
        // setBillTenantsModal(false);
    };

    // UPDATED: Enhanced billTenants function with better validation and logic
    const billTenants = async () => {
        setBillLoading(true);

        // Get only included tenants who are billing enabled AND have Include switch ON
        const includedTenants = tenants.filter(tenant => {
            const isBillingEnabled = tenant.billing_enabled === true;
            const isIncludedInBilling = tenantBillingStatus[tenant.tenant_id] === true;
            // Only include if BOTH conditions are true
            return isBillingEnabled && isIncludedInBilling;
        });

        if (includedTenants.length === 0) {
            showSnackbar({
                title: "Error!",
                message: "No tenants are included in billing.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            setBillLoading(false);
            return;
        }

        // Step 1: Validate that tenants with enabled utilities have provided readings
        const validationErrors = [];

        for (const tenant of includedTenants) {
            const settings = tenantUtilitySettings[tenant.tenant_id] || {};

            // Check if water is enabled but no reading provided
            if (settings.hasWater) {
                const hasWaterReading = billedTenants.find(t => t.tenant_id === tenant.tenant_id);
                if (!hasWaterReading) {
                    validationErrors.push(`${tenant.first_name} ${tenant.last_name} (Unit ${tenant.house_number}): Water reading required`);
                }
            }

            // Check if electricity is enabled but no reading provided
            if (settings.hasElectricity) {
                const hasElectricityReading = elecBilledTenants.find(t => t.tenant_id === tenant.tenant_id);
                if (!hasElectricityReading) {
                    validationErrors.push(`${tenant.first_name} ${tenant.last_name} (Unit ${tenant.house_number}): Electricity reading required`);
                }
            }
        }

        // If there are validation errors, show them and stop
        if (validationErrors.length > 0) {
            showSnackbar({
                title: "Missing Readings!",
                message: `Please provide readings for:\n${validationErrors.slice(0, 3).join('\n')}${validationErrors.length > 3 ? '\n...and more' : ''}`,
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            setBillLoading(false);
            return;
        }

        // Step 2: Auto-apply minimum charges for included tenants with disabled utilities
        for (const tenant of includedTenants) {
            const settings = tenantUtilitySettings[tenant.tenant_id] || {};

            // Auto-bill for water if disabled and not already billed
            if (!settings.hasWater && !billedTenants.find(t => t.tenant_id === tenant.tenant_id)) {
                setBilledTenants(prev => {
                    const filtered = prev.filter(t => t.tenant_id !== tenant.tenant_id);
                    return [...filtered, {
                        tenant_id: tenant.tenant_id,
                        rent: parseInt(tenant.rent.toString().replace(",", "")),
                        arrears: parseInt(tenant.arrears),
                        finalWaterBill: parseFloat(minimumWaterBill || 0),
                        total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(minimumWaterBill || 0),
                        meter_reading: tenant.meter_reading,
                        landlord_id: JSON.parse(originalText)[0][1],
                        isMinimumCharge: true
                    }];
                });
            }

            // Auto-bill for electricity if disabled and not already billed
            if (!settings.hasElectricity && !elecBilledTenants.find(t => t.tenant_id === tenant.tenant_id)) {
                setElecBilledTenants(prev => {
                    const filtered = prev.filter(t => t.tenant_id !== tenant.tenant_id);
                    return [...filtered, {
                        tenant_id: tenant.tenant_id,
                        rent: parseInt(tenant.rent.toString().replace(",", "")),
                        arrears: parseInt(tenant.arrears),
                        electricity_bill: parseFloat(minimumElectricityBill || 0),
                        total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(minimumElectricityBill || 0),
                        electricity_meter_reading: tenant.electricity_meter_reading,
                        landlord_id: JSON.parse(originalText)[0][1],
                        isMinimumCharge: true
                    }];
                });
            }
        }

        // Step 3: Compile final bills for included tenants only
        const finalBill = [];

        for (const tenant of includedTenants) {
            const waterBill = billedTenants.find(t => t.tenant_id === tenant.tenant_id);
            const electricityBill = elecBilledTenants.find(t => t.tenant_id === tenant.tenant_id);

            // Create bill entry for this tenant
            const billEntry = {
                tenant_id: tenant.tenant_id,
                water_bill: waterBill ? waterBill.finalWaterBill : 0,
                electricity_bill: electricityBill ? electricityBill.electricity_bill : 0,
                total_bill: 0, // Will be calculated below
                meter_reading: waterBill ? waterBill.meter_reading : 0,
                electricity_meter_reading: electricityBill ? electricityBill.electricity_meter_reading : 0,
                landlord_id: JSON.parse(originalText)[0][1],
            };

            // Calculate total bill using Wallet as arrears (only if status is PENDING)
            const rent = parseInt(tenant.rent.toString().replace(",", ""));
            // Use Wallet as arrears only if tenant status is PENDING, otherwise 0
            const arrears = tenant.status === "PENDING" ? parseFloat(tenant.Wallet || 0) : 0;

            // Calculate total bill: rent + arrears (from Wallet) + water + electricity
            billEntry.total_bill = rent + arrears + parseFloat(billEntry.water_bill) + parseFloat(billEntry.electricity_bill);

            finalBill.push(billEntry);
        }

        if (selectedMonth === null) {
            showSnackbar({
                title: "Error!",
                message: "Kindly select the month you are billing for.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            setBillLoading(false);
            return;
        }

        // Calculate estimated total for the confirmation dialog
        const estimatedTotal = finalBill.reduce((sum, bill) => sum + (bill.total_bill || 0), 0);

        // Prepare detailed tenant billing data for the confirmation dialog
        const tenantBills = finalBill.map(bill => {
            const tenant = includedTenants.find(t => t.tenant_id === bill.tenant_id);
            const rent = parseInt(tenant.rent.toString().replace(",", ""));
            // Use Wallet as arrears only if status is PENDING, otherwise 0
            const walletBalance = parseFloat(tenant.Wallet || 0);
            const arrears = tenant.status === "PENDING" ? walletBalance : 0;

            return {
                tenantName: `${tenant.first_name} ${tenant.last_name}`,
                houseNumber: tenant.house_number,
                rent: rent,
                water_bill: parseFloat(bill.water_bill || 0),
                electricity_bill: parseFloat(bill.electricity_bill || 0),
                arrears: arrears,
                wallet: 0, // Wallet is now used as arrears, so set to 0 to avoid double-counting
                total_bill: bill.total_bill,
                status: tenant.status // Include status for reference
            };
        });

        // Prepare billing data for confirmation dialog
        const billingConfirmData = {
            month: getMonthName(parseInt(month.getMonth())).toUpperCase(),
            year: parseInt(month.getFullYear()),
            propertyName: propertyDetails.property_name,
            tenantCount: finalBill.length,
            estimatedTotal: estimatedTotal,
            tenantBills: tenantBills
        };

        // Store the final bill data for later use
        setBillingDataForConfirm({
            finalBill,
            billingConfirmData
        });

        // Show the confirmation dialog
        setShowBillingConfirmDialog(true);
        setBillLoading(false);
    };

    // Function to handle confirmation
    const handleBillingConfirm = async () => {
        setShowBillingConfirmDialog(false);
        setBillLoading(true);

        const {finalBill} = billingDataForConfirm;

        //Make Billing API Call
        try {
            const response = await fetch(ApiDetails + "pegasus/visionary/property/bill/tenants", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                },
                body: JSON.stringify([...finalBill, {
                    month: parseInt(month.getMonth()),
                    year: parseInt(month.getFullYear()),
                    property_id: propertyDetails.property_id,
                    property_name: propertyDetails.property_name,
                    email_address: JSON.parse(originalText)[5][1],
                }]),
            });

            // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                const data = await response.json();
                setHtmlContent(data.htmlReport);

                showSnackbar({
                    title: "Success!",
                    message: "Tenants billed successfully",
                    type: "success",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setBillLoading(false);
            } else {
                showSnackbar({
                    title: "Error!",
                    message: "Failed billing tenants",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setBillLoading(false);
                handleClose();
            }
        } catch (err) {
            console.log(err);
            showSnackbar({
                title: "Error!",
                message: "Something went wrong while billing tenants",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            setBillLoading(false);
            handleClose();
        }
    };

    // Function to handle cancellation
    const handleBillingCancel = async () => {
        setShowBillingConfirmDialog(false);
        setBillLoading(false);

        // Enable billing if user cancels (same as original logic)
        fetch(ApiDetails + "pegasus/visionary/property/enableBilling", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
            },
            body: JSON.stringify({
                property_id: propertyDetails.property_id,
            }),
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    window.location.reload(false);
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed updating tenant's details",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Failed updating tenant's details",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    const downloadPDF = () => {
        fetch(ApiDetails + "pegasus/visionary/property/bill/report", {
            method: "POST",
            body: JSON.stringify({
                pdf: htmlContent
            }),
        })
            .then(async (response) => {
                // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "File download will start shortly",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });

                    let blob = await response.blob();
                    let href = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = href;
                    link.setAttribute("download", "billing_report.pdf");
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(href);
                    handleClose();
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed downloading report",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong while downloading report",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    return (
        <>
            <Dialog
                fullWidth
                maxWidth="xl"
                open={billTenantsModal}
                TransitionComponent={Transition}
                keepMounted
                aria-describedby="alert-dialog-slide-description"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'visible'
                    }
                }}
            >

                <DialogTitle sx={{
                    pb: 1,
                    background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.light, 0.05)})`,
                    position: 'relative'
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Box
                                sx={{
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                    p: 1,
                                    borderRadius: 2,
                                    mr: 2,
                                    color: theme.palette.success.main
                                }}
                            >
                                <ReceiptLongIcon/>
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    Bill Property Tenants
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {propertyDetails.property_name} • {propertyDetails.location}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={() => window.location.reload()} size="small">
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{pt: 3}}>
                    {htmlContent !== "" ? (
                        <Box sx={{
                            borderRadius: '12px',
                            border: '1px solid #e0e0e0',
                            padding: '24px',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            width: '100%'
                        }}>
                            <div dangerouslySetInnerHTML={{__html: htmlContent}}/>
                        </Box>
                    ) : (
                        <Box>
                            {/* Compact Header Section */}
                            <Box sx={{
                                p: 2.5,
                                backgroundColor: "#f8fafc",
                                borderRadius: 2,
                                mb: 3,
                                border: "1px solid #e2e8f0"
                            }}>
                                <Grid container spacing={3} alignItems="center">
                                    {/* Billing Month Selection */}
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1.5}}>
                                            <CalendarMonthIcon color="primary" sx={{fontSize: 20}}/>
                                            <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                                Billing Month
                                            </Typography>
                                        </Box>
                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                views={["year", "month"]}
                                                value={month}
                                                onChange={(newValue) => {
                                                    setMonth(newValue);
                                                    setSelectedMonth(newValue);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        variant="outlined"
                                                        size="small"
                                                        label=""
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    </Grid>

                                    {/* Billing Rates Info */}
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1.5}}>
                                            <InfoIcon color="info" sx={{fontSize: 20}}/>
                                            <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                                Utility Rates
                                            </Typography>
                                        </Box>
                                        <Box sx={{display: "flex", flexDirection: "column", gap: 0.5}}>
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                                <WaterDropIcon sx={{fontSize: 16, color: "#2196F3"}}/>
                                                <Typography variant="caption">
                                                    <strong>{waterBill} KSH</strong>/unit
                                                    (min: {minimumWaterBill} KSH)
                                                </Typography>
                                            </Box>
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                                <ElectricBoltIcon sx={{fontSize: 16, color: "#FF9800"}}/>
                                                <Typography variant="caption">
                                                    <strong>{electricityBill} KSH</strong>/unit
                                                    (min: {minimumElectricityBill} KSH)
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>

                                    {/* Quick Actions */}
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1.5}}>
                                            <SettingsIcon color="action" sx={{fontSize: 20}}/>
                                            <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                                Quick Actions
                                            </Typography>
                                        </Box>
                                        <Box sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<PeopleIcon sx={{fontSize: 16}}/>}
                                                onClick={() => handleBulkBillingToggle(false)}
                                                sx={{fontSize: '0.7rem', py: 0.5, px: 1}}
                                            >
                                                Exclude All
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<PeopleIcon sx={{fontSize: 16}}/>}
                                                onClick={() => handleBulkBillingToggle(true)}
                                                sx={{fontSize: '0.7rem', py: 0.5, px: 1}}
                                            >
                                                Include All
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<WaterDropIcon sx={{fontSize: 16}}/>}
                                                onClick={() => handleBulkUtilityToggle('hasWater', false)}
                                                sx={{fontSize: '0.7rem', py: 0.5, px: 1}}
                                            >
                                                No Water
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<ElectricBoltIcon sx={{fontSize: 16}}/>}
                                                onClick={() => handleBulkUtilityToggle('hasElectricity', false)}
                                                sx={{fontSize: '0.7rem', py: 0.5, px: 1}}
                                            >
                                                No Electricity
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<RefreshIcon sx={{fontSize: 16}}/>}
                                                onClick={() => {
                                                    handleBulkUtilityToggle('hasWater', true);
                                                    handleBulkUtilityToggle('hasElectricity', true);
                                                }}
                                                sx={{fontSize: '0.7rem', py: 0.5, px: 1}}
                                            >
                                                Reset Utilities
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Tenants DataGrid */}
                            <Box sx={{width: '100%', position: 'relative'}}>
                                <StyledDataGrid
                                    rows={tenants}
                                    columns={billingColumns}
                                    autoHeight
                                    disableSelectionOnClick
                                    initialState={{
                                        sorting: {sortModel: [{field: "house_number", sort: "asc"}]},
                                        pagination: {paginationModel: {pageSize: 10}}
                                    }}
                                    rowsPerPageOptions={[10]}
                                    sx={{
                                        border: "1px solid #e0e0e0",
                                        borderRadius: "8px",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                                    }}
                                />
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                {htmlContent !== "" ? (
                    <DialogActions sx={{p: 3, pt: 1}}>
                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon/>}
                            onClick={downloadPDF}
                            sx={{
                                background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                            }}
                        >
                            Download PDF
                        </Button>
                        <Button onClick={() => window.location.reload()} color="inherit">
                            Close
                        </Button>
                    </DialogActions>
                ) : (
                    <DialogActions sx={{p: 3, pt: 1}}>
                        <Button onClick={() => window.location.reload()} color="inherit">
                            Cancel
                        </Button>
                        <LoadingButton
                            onClick={billTenants}
                            loading={billLoading}
                            disabled={tenants.length === 0}
                            variant="contained"
                            startIcon={<SendIcon/>}
                            sx={{
                                borderRadius: 2,
                                px: 3,
                                background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                            }}
                        >
                            Send Bills
                        </LoadingButton>
                    </DialogActions>
                )}
            </Dialog>

            <CardBillingConfirmationDialog
                isOpen={showBillingConfirmDialog}
                onConfirm={handleBillingConfirm}
                onCancel={handleBillingCancel}
                billingData={billingDataForConfirm?.billingConfirmData || {}}
            />

            {/* Search header - replaces the old static header */}
            <Box sx={styles.searchHeader}>
                <Box>
                    <Typography variant="h6" sx={{fontWeight: 600, color: "#2C5283"}}>
                        Property Tenants • {propertyDetails.property_name || "Property"}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {filteredTenants.length} of {tenantsLength} tenants {searchTerm && "matched search criteria"}
                    </Typography>
                </Box>

                {/* Search Box */}
                <SearchBox
                    placeholder="Search by name, house number or phone..."
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{maxWidth: "400px"}}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action"/>
                            </InputAdornment>
                        ),
                        endAdornment: searchTerm && (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="clear search"
                                    onClick={handleClearSearch}
                                    edge="end"
                                    size="small"
                                >
                                    <ClearIcon fontSize="small"/>
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {!fetched ? (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                }}>
                    <CircularProgress
                        size={60}
                        thickness={4}
                        sx={{
                            color: theme.palette.primary.main,
                            mb: 2
                        }}
                    />
                    <Typography variant="h6" color="text.secondary" sx={{mb: 1}}>
                        Loading Tenants
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                        Fetching tenants ...
                    </Typography>
                </Box>
            ) : filteredTenants.length === 0 ? (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 2,
                    border: '1px solid #e0e0e0'
                }}>
                    <PeopleAltIcon
                        sx={{
                            fontSize: 60,
                            color: theme.palette.text.disabled,
                            mb: 2
                        }}
                    />
                    <Typography variant="h6" color="text.secondary" sx={{mb: 1}}>
                        No Tenants Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        There are no tenants in this property yet.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{
                    width: '100%',
                    marginBottom: 4,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    borderRadius: "8px",
                    overflow: "hidden"
                }}>
                    <StyledDataGrid
                        rows={filteredTenants}
                        columns={columns}
                        disableSelectionOnClick
                        onRowClick={(params) => {
                            navigate(`/tenant/${params.row.tenant_id}`, {
                                state: {tenant_id: params.row.tenant_id, property_id: params.row.property_id},
                            });
                        }}
                        initialState={{
                            sorting: {sortModel: [{field: "house_number", sort: "asc"}]},
                            pagination: {
                                paginationModel: {
                                    pageSize: 10
                                }
                            }
                        }}
                        rowsPerPageOptions={[10]}
                        autoHeight
                        sx={{
                            border: "1px solid #e0e0e0",
                            // Add cursor pointer to indicate rows are clickable
                            "& .MuiDataGrid-row": {
                                cursor: "pointer"
                            },
                            // Optional: Add hover effect to make it more obvious
                            "& .MuiDataGrid-row:hover": {
                                backgroundColor: "#e3f2fd !important"
                            }
                        }}
                    />
                </Box>
            )}
        </>
    );
};

export default PropertyTenants;